import type { Messages } from "../types.js";

/**
 * 简体中文词条。键必须与 `en.ts` 完全一致——少一个或多一个，TypeScript 都会报错。
 *
 * 约定：
 *  - `{name}` 是占位符，顺序可按中文语序调整，但名字不能改。
 *  - 复数键（`.one` / `.other`）中文两条写同样的文案：`Intl.PluralRules("zh-CN")`
 *    永远返回 `other`，`.one` 只是为了满足类型完整性。
 *  - 技术标识（`decision_id`、`change:manage`、`--infer`、ID 前缀等）保持原样。
 *
 * 文案风格（Apple Style Guide + HIG Writing，对应 `en.ts`）：
 *  - 标点全部用全角；省略号只用一个“…”；中文与西文、数字之间留一个半角空格。
 *  - 正文句末加句号；标题、标签、按钮、表头不加。
 *  - 称呼用“你”，不用“您”；不用叹号；不用“我们”指代产品本身；能省的“请”都省掉。
 *  - 少用“被”字被动式和“此／该”这类公文腔，用“这个／这条”。
 *  - 一处说明只写一句。标题够用就不要再解释。不讲产品哲学，不写路线图，不引用内部编号。
 *  - 空状态直接说“没有 X”，不说“还没有”。占位符给例子或短语，不写成问句。
 *  - 通用界面词沿用 Apple 简体中文的说法：拷贝、存储、电子邮件、账户、点按、
 *    正在载入。领域词保持产品自己的一套：决策、约束、变更、模块、证据、记忆、
 *    工作区、智能体、Pull Request、发现、漂移、修订、溯源。约束不叫“规矩”或“规则”，
 *    “规则”只指审批规则；retire 统一译“停用”；component 译“模块”，不译“组件”，
 *    模块类型里的 module 译“子模块”；路径“映射”到模块，不说“认领”；
 *    连接器是“探索”，不是“同步”；账号一律写“账户”，邮箱写“电子邮件”。
 *  - 破坏性确认按钮重复动词并点明对象（“吊销密钥”）。
 *  - 数据词汇（`status.*`、`role.*` 等）是引擎报上来的值，保持短，不加标点。
 *  - 说不知道就说不知道：记忆里没有内容时，文案要讲明这不等于放行。
 */
export const zhCN: Messages = {
  // ------------------------------------------------------------------ 通用
  "common.back": "返回",
  "common.cancel": "取消",
  "common.confirm": "确认",
  "common.close": "关闭",
  "common.dismiss": "忽略",
  "common.copy": "拷贝",
  "common.loading": "正在载入…",
  "common.none": "—",
  "common.nothingToReview": "没有需要评审的内容。",
  "common.unsavedChanges": "有未保存的更改",

  // ------------------------------------------------------------------ 语言
  "language.label": "语言",

  // ------------------------------------------------------------------ 外观
  "appearance.label": "外观",
  "appearance.system": "跟随系统",
  "appearance.light": "浅色",
  "appearance.dark": "深色",

  // -------------------------------------------------------------- 错误状态
  "error.title": "出错了",
  "error.notFound": "未找到",
  "error.status": "错误 {status}",
  "error.forbidden": "你没有访问这个工作区的权限。",
  "error.network": "无法连接",
  "error.requestFailed": "无法完成请求",
  "error.requestId": "请求 {id}",
  "error.backToWorkspaces": "返回工作区",

  // ---------------------------------------------------------------- 404
  "notFound.workspace": "你不是这个工作区的成员，或者该工作区不存在。",
  "notFound.title": "找不到页面",
  "notFound.hint": "这个页面不存在。",
  "notFound.action": "返回工作区",

  // -------------------------------------------------------------- 登录
  "signIn.title": "登录",
  "signIn.subtitle": "工程决策记忆。",
  "signIn.github": "使用 GitHub 继续",
  "signIn.or": "或",
  "signIn.submit": "登录",
  "signIn.failed": "无法登录。检查电子邮件和密码，然后再试一次。",
  "signIn.noAccount": "没有账户？{link}",
  "signIn.signUpLink": "注册",

  // -------------------------------------------------------------- 注册
  "signUp.title": "创建账户",
  "signUp.subtitle": "加入或创建工作区。",
  "signUp.submit": "注册",
  "signUp.failed": "无法创建账户。再试一次。",
  "signUp.passwordHint": "至少 10 个字符。",
  "signUp.namePlaceholder": "张三",
  "signUp.haveAccount": "已有账户？{link}",
  "signUp.signInLink": "登录",

  // ------------------------------------------------------------ 身份字段
  "field.name": "姓名",
  "field.email": "电子邮件",
  "field.password": "密码",
  "field.emailPlaceholder": "you@company.com",

  // ---------------------------------------------------------------- 邀请
  "invitation.joining": "正在加入工作区…",
  "invitation.errorTitle": "无法接受邀请",
  "invitation.invalid": "此邀请对当前账户无效。",
  "invitation.goToWorkspaces": "返回工作区",

  // ------------------------------------------------------------ 新建工作区
  "workspaceNew.title": "创建工作区",
  "workspaceNew.subtitle": "一个工作区对应一个组织。",
  "workspaceNew.name": "工作区名称",
  "workspaceNew.namePlaceholder": "Acme 工程团队",
  "workspaceNew.slug": "URL 标识",
  "workspaceNew.slugPlaceholder": "acme",
  "workspaceNew.slugHint": "小写字母、数字和连字符。用于 /w/<slug>。",
  "workspaceNew.submit": "创建工作区",
  "workspaceNew.slugTaken": "此 URL 标识已被使用。",
  "workspaceNew.slugTakenHint": "换一个标识。",

  // ================================================================ 初始化工作区（D-0022）
  "setup.title": "连接来源",
  "setup.subtitle": "从这些来源找出团队已做的决策，交由你确认。",
  "setup.connect": "连接",
  "setup.connected": "已连接",
  "setup.upload": "上传文件",
  "setup.upload.hint": "Markdown 或纯文本",
  "setup.upload.choose": "选择文件",
  "setup.upload.clear": "清除",
  "setup.upload.count.one": "{count} 个文件",
  "setup.upload.count.other": "{count} 个文件",
  "setup.upload.tooLarge": "{name} 超过 500 KB，未添加。",
  "setup.next": "下一步",
  "setup.skip": "跳过",
  "setup.tab.manual": "手动添加",
  "setup.tab.agent": "Agent 自动添加",
  "setup.agent.hint": "拷贝给 Claude Code、Codex 等编码智能体。",
  "setup.agent.prompt": "帮我把组织「{workspace}」已有的工程决策整理进 Diotia。\n\n1. 安装 Diotia 的 MCP 服务器：名称 diotia，HTTP 传输，地址 {url}\n   - Claude Code：claude mcp add --transport http diotia {url}\n   - Codex：codex mcp add diotia --url {url}\n2. 授权：连接时会在浏览器里打开 Diotia，我在那里选择工作区「{workspace}」并同意。Codex 用 codex mcp login diotia 发起；Claude Code 在 /mcp 里认证。如果这个会话还用不了 diotia 的工具，告诉我下一步怎么做，授权成功后再继续。\n3. 找出组织「{workspace}」的代码仓库（例如用 gh repo list 查这个组织，或看我本地已克隆的仓库），逐个阅读 README、文档、ADR、提交记录和 Pull Request，找出已经做出的工程决策：技术选型、架构、服务边界、对外接口、数据存储、难以回退的依赖。修 bug、重构、依赖升级不算。\n4. 每条决策先用 search_decisions 查重，再用 propose_decision 提议：\n   - title、outcome：决定了什么\n   - rationale：只写原文写明的理由，没有就留空，不要编造\n   - scope：所在的仓库或服务\n   - evidence：来源链接（文件、提交或 Pull Request）和原文摘录\n   只提议，不批准。\n5. 完成后告诉我看了哪些仓库、提议了几条决策。",
  "setup.agent.copied": "提示已拷贝",
  "setup.agent.copyFailed": "无法访问剪贴板。选中提示并拷贝。",
  "setup.agent.done": "完成",
  "setup.readOnly": "只有所有者和管理员可以设置工作区。",
  "setup.failed": "上次未能读取来源",
  "setup.error.no_pages": "没有可读取的页面。",
  "setup.error.unauthorized": "授权已失效。重新连接。",
  "setup.error.rate_limited": "请求过于频繁。稍后再试。",
  "setup.error.timed_out": "读取超时。再试一次。",
  "setup.error.model_not_configured": "服务器没有配置用于探索的模型。",
  "setupGate.running": "正在设置工作区…",
  "setupGate.pending": "先设置工作区",
  "setupGate.add": "连接来源",

  // ---------------------------------------------------------- 工作区框架
  "workspace.switcherAria": "工作区：{name}",
  "workspace.switch": "切换工作区",
  "workspace.new": "新建工作区",
  "user.menuAria": "账户菜单",
  "user.signedIn": "已登录",
  "user.signOut": "退出登录",
  "nav.aria": "工作区导航",
  // 每一项都是你要做的事。模块和约束不在这里，它们在发现和级联里被遇到。
  "nav.getStarted": "开始使用",
  "nav.inbox": "收件箱",
  "nav.reviews": "审批",
  "nav.drafts": "草稿",
  "nav.myChanges": "我的变更",
  "nav.decisions": "决策",
  "nav.health": "健康",
  "nav.settings": "设置",
  // ---------------------------------------------------------------- 决策
  "decisions.title": "决策",
  "decisions.description": "已接受的决策只能通过修订或后继决策改变。",
  "decisions.exportAll": "全部导出",
  "decisions.import": "导入 Markdown",
  "decisions.draft": "起草决策",
  "decisions.empty.title": "没有决策",
  "decisions.empty.hint": "起草决策，或导入 Markdown 记录。",
  "decisions.nextPage": "下一页",
  "decisions.th.id": "ID",
  "decisions.th.title": "标题",
  "decisions.th.status": "状态",
  "decisions.th.scope": "范围",
  "decisionNew.title": "起草决策",
  "decisionNew.successorTitle": "起草 {id} 的后继决策",
  "decisionNew.description": "只有人能接受决策。",
  "decisionNew.submit": "存储草稿",

  // ------------------------------------------------------------ 决策表单
  "decisionForm.title": "标题",
  "decisionForm.titlePlaceholder": "统一使用 PlanetScale PostgreSQL 作为唯一关系型存储",
  "decisionForm.scope": "范围",
  "decisionForm.scopePlaceholder": "仓库、服务或模块名",
  "decisionForm.scopeHint": "留空表示整个工作区。",
  "decisionForm.context": "背景",
  "decisionForm.contextPlaceholder": "需要决策的背景",
  "decisionForm.problem": "问题",
  "decisionForm.problemPlaceholder": "要决定的问题",
  "decisionForm.outcome": "决策",
  "decisionForm.outcomePlaceholder": "选择的方案",
  "decisionForm.rationale": "理由",
  "decisionForm.rationalePlaceholder": "选择的理由",
  "decisionForm.supersedes": "取代",
  "decisionForm.supersedesHint": "接受后会取代这些决策。",
  "decisionForm.dependsOn": "依赖",

  // ------------------------------------------------------------ 决策详情
  "decision.approvalRecorded": "已记录你的批准：{have}/{required}。满足审批规则后生效。",
  "decision.retirementRequested": "已提出停用请求 {ref}。满足审批规则后生效。",
  "decision.reviewsLink": "打开审批",
  "decision.unknownAction": "未知操作",
  "decision.edit": "编辑",
  "decision.propose": "提议",
  "decision.reject": "拒绝",
  "decision.accept": "接受",
  "decision.downloadMarkdown": "下载 Markdown",
  "decision.draftSuccessor": "起草后继决策",
  "decision.superseded.title": "已取代",
  "decision.superseded.body": "作为历史保留。已被 {links} 取代。",
  "decision.kv.scope": "范围",
  "decision.kv.workspaceWide": "整个工作区",
  "decision.kv.origin": "来源",
  "decision.kv.revision": "修订号",
  "decision.kv.createdBy": "创建者",
  "decision.kv.approvedBy": "批准人",
  "decision.kv.supersedes": "取代",
  "decision.kv.supersededBy": "取代者",
  "decision.kv.dependsOn": "依赖",
  "decision.section.context": "背景",
  "decision.section.problem": "问题",
  "decision.section.outcome": "决策",
  "decision.section.rationale": "理由",
  "decision.emptyBody": "没有正文。在草稿中补充背景、决策和理由。",
  "decision.alternatives": "考虑过的方案",
  "decision.assumptions": "假设",
  "decision.consequences": "后果",
  "decision.lineage.title": "取代链",
  "decision.lineage.earlier": "更早",
  "decision.lineage.later": "更晚",
  "decision.constraints.title": "引入的约束",
  "decision.constraints.th.id": "ID",
  "decision.constraints.th.statement": "约束内容",
  "decision.constraints.th.status": "状态",
  "decision.constraints.keptBy": "（由 {id} 保留）",
  "decision.constraints.replacedBy": "（由 {id} 替换）",
  "decision.deprecate.summary": "废弃决策",
  "decision.deprecate.reason": "原因",
  "decision.deprecate.reasonHint": "决策记录会保留，其约束不再生效。",
  "decision.deprecate.submit": "废弃",
  "decision.history.title": "历史",
  "decision.history.description": "每条都是当时的快照，不会被修改。",
  "decision.history.actor": "{actor}（{kind}）",
  "decision.history.policy": "策略 {id}",
  "decision.history.human": "人工",
  "decision.amendments.title": "修订",
  "decision.amendments.description": "补充已接受的决策，不改变结论。",
  "decision.amendments.by": "提出者：{actor}",
  "decision.amendments.addedAssumption": "+ 假设：{text}",
  "decision.amendments.accept": "接受 {ref}",
  "decision.amendments.reject": "拒绝 {ref}",
  "decision.amendments.propose": "提议修订",
  "decision.amendments.summary": "摘要",
  "decision.amendments.clarification": "澄清说明",
  "decision.amendments.newAssumptions": "新增假设",
  "decision.amendments.onePerLine": "每行一条。",
  "decision.amendments.submit": "提议修订",
  "decisionEdit.title": "编辑决策",
  "decisionEdit.description": "编辑记入历史。已接受的决策通过修订或后继决策改变。",
  "decisionEdit.submit": "存储",

  // ------------------------------------------------------- Markdown 导入
  "import.title": "导入 Markdown",
  "import.description": "沿用文件中的 decision_id；没有则分配新 ID。只有所有者和管理员可以导入。",
  "import.noFiles": "选择至少一个 Markdown 文件。",
  "import.files": "文件",
  "import.filesHint": "ADR、RFD 或决策 Markdown（Nygard、MADR、Diotia）。",
  "import.filesAction": "选择文件",
  "import.filesEmpty": "未选择文件。也可拖到这里。",
  "import.filesClear": "清除",
  "import.filesCount.one": "{count} 个文件",
  "import.filesCount.other": "{count} 个文件",
  "import.repository": "代码仓库",
  "import.repositoryPlaceholder": "https://github.com/acme/api",
  "import.repositoryHint": "可选。文件来源。",
  "import.onConflict": "发生冲突时",
  "import.onConflict.fail": "报告并跳过",
  "import.onConflict.preferCloud": "保留记忆中的版本",
  "import.onConflict.preferFile": "使用文件（仅草稿和提案）",
  "import.preview": "预览",
  "import.apply": "导入",
  "import.previewTitle": "导入预览",
  "import.resultTitle": "导入结果",
  "import.previewNote": "导入将执行以下操作。",
  "import.th.file": "文件",
  "import.th.decision": "决策",
  "import.th.action": "操作",
  "import.th.details": "详情",
  "import.conflictLine": "{field}：文件“{file}” · 记忆“{cloud}”",

  // ---------------------------------------------------------------- 变更
  "changes.th.change": "变更",
  "changes.th.scope": "范围",
  "changes.th.status": "状态",
  "change.reviewAgain": "重新评审",
  "change.reviewed.one": "已评审 {revision}：{count} 条发现。",
  "change.reviewed.other": "已评审 {revision}：{count} 条发现。",
  "change.dismissed": "已忽略。不会再出现。",
  "change.exceptionRecorded": "已记录为可接受的例外。",
  "change.confirmed": "已确认。",
  "change.kv.status": "状态",
  "change.kv.scope": "范围",
  "change.kv.references": "关联链接",
  "change.kv.components": "模块",
  "change.components.none": "未映射",
  "change.component": "{name}（{files}，{provenance}）",
  "change.componentFiles.one": "{count} 个文件",
  "change.componentFiles.other": "{count} 个文件",
  "change.noFindings.title": "没有发现",
  "change.noFindings.hint": "与已接受的决策和生效的约束没有冲突。",
  "change.uncertainty.title": "记忆中没有内容",
  "change.uncertainty.body": "这不等于批准。要留下长期结论，记录一条决策。",
  "finding.verified": "依据记录核实",
  "finding.readFromDescription": "从描述中读取",
  "finding.meta": "{severity} · 置信度 {confidence}%",
  "finding.basedOn": "依据：",
  "finding.confirm": "确认",
  "finding.falsePositive": "标为误报",
  "finding.acceptedException": "接受为例外",

  // ---------------------------------------------------------------- 模块
  "component.kv.kind": "类型",
  "component.kv.status": "状态",
  "component.kv.aliases": "别名",
  "component.kv.keys": "标识键",
  "component.kv.origin": "来源",
  "component.locations.title": "源码位置",
  "component.locations.description": "属于这个模块的路径。拒绝过的映射不会再次推断。",
  "component.locations.anyRepository": "任意仓库",
  "component.locations.confirm": "确认",
  "component.locations.reject": "拒绝",
  "component.locations.path": "路径",
  "component.locations.repository": "代码仓库",
  "component.locations.pathPlaceholder": "services/api/ 或 **/*.proto",
  "component.locations.map": "映射",
  "component.activeDecisions": "生效中的决策",
  "component.pendingDecisions": "待定的决策",
  "component.historicalDecisions": "历史",
  "component.constraints": "约束",

  // ---------------------------------------------------------------- 接入
  "onboarding.description": "推送快照，再确认要保留的内容。建议不会自动成为已接受的记忆。",
  "onboarding.rejected": "已拒绝。不会再次建议。",
  "onboarding.created": "已创建 {id}。",
  "onboarding.open": "打开 {id}",
  "onboarding.connect.title": "连接代码仓库",
  "onboarding.connect.intro": "创建具备 evidence:record 权限的 API 密钥，然后在仓库或每次合并后的 CI 中运行：",
  "onboarding.connect.infer": "添加 --infer，从提交和 Pull Request 中推断决策（需要 integration:manage）。",
  "onboarding.empty.title": "没有快照",
  "onboarding.empty.hint": "用上方的命令推送一份。",
  "onboarding.run.title": "最近一次运行",
  "onboarding.run.status": "状态",
  "onboarding.run.revision": "版本",
  "onboarding.run.finished": "完成时间",
  "onboarding.run.documents": "文档",
  "onboarding.run.documentsNone": "无",
  "onboarding.run.documentsItem": "{count} 份{kind}",
  "onboarding.run.history": "历史",
  "onboarding.run.historyValue": "{commits} 次提交，{changes} 个 Pull Request",
  "onboarding.run.candidates": "候选项",
  "onboarding.run.candidatesValue": "{decisions} 条决策、{constraints} 条约束、{components} 个模块（{inMemory} 条已在记忆中，{suppressed} 条此前被拒绝）",
  "onboarding.run.inference": "模型推断",
  "onboarding.run.inferenceValue": " 保留 {kept} 条，因缺少引用丢弃 {dropped} 条",
  "onboarding.run.error": "错误",
  "onboarding.run.unresolved": "未能解析",
  "onboarding.decisionCandidates": "候选决策（{count}）",
  "onboarding.constraintCandidates": "候选约束（{count}）",
  "onboarding.constraintCandidates.description": "来自智能体指令文件。",
  "onboarding.componentCandidates": "候选模块（{count}）",
  "onboarding.componentCandidates.description": "来自包清单和 .diotia/components.yaml。",
  "onboarding.source.inferred": "推断 · {confidence}%",
  "onboarding.source.repositoryRecord": "仓库内记录",
  "onboarding.source.declared": "显式声明",
  "onboarding.source.manifest": "清单 · {confidence}%",
  "onboarding.idConflict": "记忆中已有另一条决策使用这个 decision_id。",
  "onboarding.reject": "拒绝",
  "onboarding.confirm": "确认",
  "onboarding.confirmAndActivate": "确认并生效",

  // ---------------------------------------------------------------- 成员
  "members.title": "成员",
  "members.invite": "邀请成员",
  "members.invite.title": "邀请成员",
  "members.invite.description": "发送带加入链接的电子邮件。",
  "members.invite.emailPlaceholder": "teammate@company.com",
  "members.invite.role": "角色",
  "members.invite.roleHint": "管理员可以管理成员和 API 密钥。",
  "members.invite.roleAria": "角色",
  "members.invite.send": "发送邀请",
  "members.invite.failed": "无法发送邀请。再试一次。",
  "members.invite.sent": "邀请已发送",
  "members.count.one": "{count} 位成员",
  "members.count.other": "{count} 位成员",
  "members.empty.title": "没有成员",
  "members.pending.title": "待接受的邀请",
  "members.pending.description": "邀请会过期。再邀请一次可延长。",
  "members.pending.expires": "{date} 过期",

  // -------------------------------------------------------------- API 密钥
  "agents.title": "编码智能体",
  "agents.description": "你连接的 Claude Code、Codex 等 MCP 客户端会以你的身份在这里工作。它们可以提议决策，但不能批准。",
  "agents.connect.title": "连接",
  "agents.connected.title": "你连接的智能体",
  "agents.empty.title": "没有连接的智能体",
  "agents.empty.hint": "把上面的服务器添加到你的智能体。它会打开 Diotia，让你选择工作区。",
  "agents.connectedAt": "连接于 {date}",
  "agents.disconnect": "断开",
  "agents.disconnect.title": "断开“{name}”？",
  "agents.disconnect.description": "它会立即失去访问权限。要重新连接，从智能体里重新发起。",
  "agents.disconnect.confirm": "断开智能体",
  "agents.disconnected": "已断开智能体",
  "oauthConsent.title": "连接 {client}",
  "oauthConsent.subtitle": "{client} 将以你的身份使用 Diotia。",
  "oauthConsent.unnamed": "一个智能体",
  "oauthConsent.workspace": "工作区",
  "oauthConsent.can": "可以：读取决策与约束、检查计划、提议决策。",
  "oauthConsent.cannot": "不能：批准、否决或投票。",
  "oauthConsent.connect": "连接",
  "oauthConsent.noWorkspace": "先创建一个工作区。",
  "oauthConsent.invalidTitle": "链接已失效",
  "oauthConsent.invalid": "回到你的智能体重新发起。",
  "apiKeys.title": "API 密钥",
  "apiKeys.create": "创建密钥",
  "apiKeys.create.title": "创建 API 密钥",
  "apiKeys.create.description": "以智能体和机器命名。密钥只显示一次。",
  "apiKeys.create.name": "密钥名称",
  "apiKeys.create.namePlaceholder": "笔记本上的 claude-code",
  "apiKeys.minted.title": "立即拷贝这个密钥。它不会再显示。",
  "apiKeys.copyTooltip": "拷贝到剪贴板",
  "apiKeys.copyAria": "拷贝密钥",
  "apiKeys.copied": "密钥已拷贝",
  "apiKeys.clipboardUnavailable": "无法访问剪贴板",
  "apiKeys.clipboardHint": "选中密钥并拷贝。",
  "apiKeys.noneCreated": "没有已创建的密钥。",
  "apiKeys.count.one": "{count} 个有效密钥",
  "apiKeys.count.other": "{count} 个有效密钥",
  "apiKeys.empty.title": "没有 API 密钥",
  "apiKeys.empty.hint": "创建密钥，供智能体读取这个工作区。",
  "apiKeys.created": "创建于 {date}",
  "apiKeys.revoke": "吊销",
  "apiKeys.revoke.title": "吊销“{name}”？",
  "apiKeys.revoke.description": "正在使用这个密钥的智能体会立即失去访问权限。无法撤销。",
  "apiKeys.revoke.confirm": "吊销密钥",
  "apiKeys.revoked": "密钥已吊销",
  "connectors.title": "连接器",
  "connectors.group.notInstalled": "未安装",
  "connectors.empty": "暂无连接器。",
  "connectors.status.enabled": "已启用",
  "connectors.status.suspended": "已暂停",
  "connectors.status.requested": "已申请",
  "connectors.status.unavailable": "不可用",
  "connectors.installation": "安装",
  "connectors.links.title": "已关联账户",
  "connectors.install": "安装",
  "connectors.installed": "{name} 安装成功",
  "connectors.installFailed": "无法安装 {name}",
  "connectors.reason.denied": "安装已取消。",
  "connectors.reason.forbidden": "只有发起安装的所有者或管理员可以完成安装。",
  "connectors.reason.signed_out": "回到 Diotia 时没有登录状态，安装没有完成。",
  "connectors.reason.already_bound": "这个账户已绑定到另一个 Diotia 工作区。",
  "connectors.reason.failed": "再试一次。",
  "connectors.notInstalled": "未安装。",
  "connectors.notInstalled.notion": "未安装。在 Notion 授权时勾选顶层页面，其下所有子页面都会包含。",
  "connectors.notConfigured": "此服务器未启用。",
  "connectors.installedOn": "安装于 {date}",
  "connectors.readOnly": "只有所有者和管理员可以安装或移除连接器。",
  "connectors.remove": "移除",
  "connectors.remove.title": "移除 {name}？",
  "connectors.remove.description": "Diotia 也会从 {name} 上移除，所有已关联的账户都会解除关联。",
  "connectors.remove.confirm": "移除 {name}",
  "connectors.removed": "{name} 已移除",
  "connectors.links.empty": "没有已关联的账户。成员首次与智能体对话时，会按电子邮件地址自动关联。",
  "connectors.links.method.email": "电子邮件",
  "connectors.links.method.manual": "手动",
  "connectors.links.inactive": "已失效",
  "connectors.links.left": "已离开工作区",
  "connectors.links.unlink": "解除关联",
  "connectors.links.unlink.title": "解除 {name} 的关联？",
  "connectors.links.unlinked": "已解除关联",
  "connectors.linked": "{name} 账户已关联",
  "connectors.updated": "{name} 设置已更新",
  "connectors.requested": "已申请，需所有者在 {name} 上批准。",
  "connectors.pending": "已申请，等待所有者在 {name} 上批准。",
  "connectors.configure": "在 {name} 上配置",
  "connectors.resources.all.repository": "所有仓库",
  "connectors.resources.all.page": "所有页面",
  "connectors.resources.all.generic": "全部",
  "connectors.resources.count.repository.one": "{count} 个仓库",
  "connectors.resources.count.repository.other": "{count} 个仓库",
  "connectors.resources.count.page.one": "{count} 个页面",
  "connectors.resources.count.page.other": "{count} 个页面",
  "connectors.resources.count.page.atLeast": "{count}+ 个页面",
  "connectors.resources.count.generic.one": "{count} 项",
  "connectors.resources.count.generic.other": "{count} 项",
  "connectors.resources.more": "另有 {count} 个",
  "connectors.identify": "关联我的 {name} 账户",
  "connectors.identify.label": "你的 {name} 账户",
  "connectors.identify.linkedAs": "已关联为 {name}",
  "connectors.identify.unlinked": "未关联。",
  "connectors.identifyFailed": "无法关联 {name}",
  "connectors.links.emptyIdentify": "没有已关联的账户。",
  "connectors.links.method.account": "登录账户",
  "connectors.links.method.oauth": "已验证",
  "connectors.sync.title": "查找决策",
  "connectors.sync.now": "立即探索",
  "connectors.sync.never": "从未探索",
  "connectors.sync.started": "开始探索 {name}",
  "connectors.sync.failed": "无法探索 {name}",
  "connectors.sync.status.queued": "排队中",
  "connectors.sync.status.reading": "探索中",
  "connectors.sync.status.processing": "处理中",
  "connectors.sync.status.succeeded": "已完成",
  "connectors.sync.status.failed": "失败",
  "connectors.sync.documents.one": "读取 {count} 页",
  "connectors.sync.documents.other": "读取 {count} 页",
  "connectors.sync.coverage": "看到 {listed} 项，打开 {read} 项",
  "connectors.sync.candidates.one": "{count} 条候选决策",
  "connectors.sync.candidates.other": "{count} 条候选决策",
  "connectors.sync.skipped.restricted": "跳过 {count} 个受限页面",
  "connectors.sync.skipped.overLimit": "{count} 页超出上限",
  "connectors.sync.skipped.unreadable": "{count} 页无法读取",
  "connect.summary": "将 {provider} 账户 {name}（{team}）关联到 {workspace}？",
  "connect.confirm": "关联账户",
  "connect.expiredTitle": "链接已过期",
  "connect.expired": "向智能体重新索取链接。",

  // ---------------------------------------------------------------- 任务
  "operation.title": "任务",
  "operation.back": "概览",
  "operation.details": "详情",
  "operation.kv.type": "类型",
  "operation.kv.attempts": "尝试次数",
  "operation.kv.attemptsValue": "第 {attempts} 次，共 {max} 次",
  "operation.kv.state": "状态",
  "operation.state.terminal": "已结束",
  "operation.state.inProgress": "进行中",
  "operation.lastError": "最近一次错误",
  "operation.waiting": "正在等待任务完成…",

  // ------------------------------------------------------- 数据词汇：状态
  "status.draft": "草稿",
  "status.proposed": "已提议",
  "status.accepted": "已接受",
  "status.rejected": "已拒绝",
  "status.superseded": "已取代",
  "status.deprecated": "已废弃",
  "status.active": "生效中",
  "status.inactive": "未生效",
  "status.retired": "已停用",
  "status.retracted": "已撤回",
  "status.suggested": "待审",
  "status.withdrawn": "已撤回",
  "status.confirmed": "已确认",
  "status.planned": "已规划",
  "status.in_progress": "进行中",
  "status.merged": "已合并",
  "status.reverted": "已回滚",
  "status.abandoned": "已放弃",
  "status.queued": "排队中",
  "status.running": "运行中",
  "status.pending": "等待中",
  "status.succeeded": "已完成",
  "status.failed": "失败",
  "status.dead": "已停止",
  "status.not_requested": "未请求",
  "status.not_configured": "未配置",

  // ------------------------------------------------------- 数据词汇：角色
  "role.owner": "所有者",
  "role.admin": "管理员",
  "role.member": "成员",
  "actor.user": "用户",
  "actor.api_key": "智能体",
  "actor.service": "服务",

  // ------------------------------------------------------- 数据词汇：来源
  "provenance.imported": "导入",
  "provenance.inferred": "推断",
  "provenance.human_confirmed": "人工确认",
  "origin.authored": "人工撰写",
  "origin.imported": "导入",
  "origin.inferred": "推断",

  // --------------------------------------------------- 数据词汇：模块类型
  "componentKind.service": "服务",
  "componentKind.module": "子模块",
  "componentKind.api": "接口",
  "componentKind.datastore": "数据存储",
  "componentKind.library": "库",
  "componentKind.workflow": "工作流",
  "componentKind.capability": "能力",

  // --------------------------------------------------- 数据词汇：决策历史
  "event.created": "创建",
  "event.edited": "编辑",
  "event.transitioned": "状态变更",
  "event.amendment_proposed": "提议修订",
  "event.amendment_accepted": "接受修订",
  "event.amendment_rejected": "拒绝修订",
  "event.amendment_withdrawn": "撤回修订",
  "event.approval_recorded": "记录批准",
  "event.retirement_proposed": "提议停用",
  "event.retirement_accepted": "接受停用",
  "event.retirement_declined": "驳回停用",
  "event.retirement_withdrawn": "撤回停用",

  // --------------------------------------------------- 数据词汇：评审语义
  "findingClass.conformance": "符合",
  "findingClass.conflict": "冲突",
  "findingClass.decision_gap": "决策缺口",
  "findingClass.drift_candidate": "疑似漂移",
  "findingClass.ambiguity": "不明确",
  "severity.low": "低",
  "severity.medium": "中",
  "severity.high": "高",
  "severity.critical": "严重",
  "consequence.positive": "正面",
  "consequence.negative": "负面",
  "consequence.neutral": "中性",

  // --------------------------------------------------- 数据词汇：约束类别
  "constraintCategory.dependency_boundary": "依赖边界",
  "constraintCategory.security": "安全",
  "constraintCategory.privacy": "隐私",
  "constraintCategory.consistency": "一致性",
  "constraintCategory.compatibility": "兼容性",
  "constraintCategory.performance": "性能",
  "constraintCategory.data_ownership": "数据归属",
  "constraintCategory.operational": "运维",
  "constraintCategory.product_invariant": "产品不变量",
  "constraintCategory.migration": "迁移",

  // --------------------------------------------------- 数据词汇：导入动作
  "importAction.created": "新建",
  "importAction.updated": "更新",
  "importAction.unchanged": "未变化",
  "importAction.conflict": "冲突",
  "importAction.skipped": "跳过",
  "importAction.invalid": "无效",

  // --------------------------------------------------------------- 溯源
  "lineage.title": "溯源",
  "lineage.mappedBy": "命中 {pattern}（{provenance}）",
  "lineage.introducedBy": "由 {decision} 引入",
  "lineage.acceptedAt": "{date} 接受",

  // ------------------------------------------------------------- 决策提案
  "change.significance.title": "架构显著性",
  "change.significance.worthy": "可能值得记录",
  "change.significance.routine": "常规",
  "change.significance.supersedes": "将取代：{ids}",
  "change.proposal.draft": "起草提案",
  "change.proposal.drafted": "已起草 {id}。接受前仍为草稿。",
  "change.proposal.exists": "已有草稿 {id}。",
  "change.proposal.notNeeded": "不需要决策记录。",

  // ------------------------------------------------------------ 架构 CI
  "ci.metrics.trust": "评审结果",
  "ci.metrics.falsePositiveRate": "误报率",
  "ci.metrics.acceptedFindingRate": "采纳率",
  "ci.metrics.unknown": "未测量",
  "ci.drift.confirm": "确认",
  "ci.drift.dismiss": "不是漂移",
  "driftKind.repeated_exception": "反复豁免",
  "driftKind.unmapped_component": "模块未映射",
  "driftKind.unenforced_rule": "约束未执行",
  "driftKind.stale_decision": "决策陈旧",
  "driftKind.orphan_constraint": "孤立约束",

  // --------------------------------------------------- 数据词汇：文档类型
  "documentKind.decision_record": "决策记录",
  "documentKind.instruction_file": "指令文件",
  "documentKind.spec": "规格文档",
  "documentKind.manifest": "包清单",
  "documentKind.component_map": "模块映射",

  // ============================================================ 开始使用
  "getStarted.title": "开始使用",
  "getStarted.progress": "已完成 {done}/{total}",
  "getStarted.source.title": "添加决策来源",
  "getStarted.source.body": "连接 Notion、Confluence，或上传文档。",
  "getStarted.source.running": "正在读取来源…",
  "getStarted.source.action": "添加来源",
  "getStarted.decisions.title": "确认决策",
  "getStarted.decisions.body": "找到的决策要经你确认才生效。",
  "getStarted.decisions.action.drafts": "去草稿",
  "getStarted.decisions.action.reviews": "去审批",
  "getStarted.agent.title": "接入编码智能体",
  "getStarted.agent.body": "Claude Code、Codex 改代码前会先查这些决策。",
  "getStarted.agent.action": "连接智能体",
  "getStarted.checks.title": "开启 Pull Request 检查",
  "getStarted.checks.body": "每个 Pull Request 都会对照已确认的决策检查。",
  "getStarted.checks.action": "连接 GitHub",

  // ============================================================== 收件箱
  "inbox.title": "收件箱",
  "inbox.description": "等你处理的事项。",
  "inbox.empty.title": "没有需要你处理的事",
  "inbox.empty.hint": "你负责的模块上的冲突、快到期的例外、向你提的问题，都会出现在这里。",
  "inbox.empty.setup": "Pull Request 检查未开启，所以这里不会有内容。",
  "inbox.conflict.title": "{component} 上有新发现",
  "inbox.conflict.open": "打开 Pull Request",
  "inbox.conflict.falsePositive": "标为误报",
  "inbox.conflict.where": "在 Pull Request 里处理。有人处理之前，它会一直显示在这里。",
  "inbox.drift.title": "值得看一眼的漂移",
  "inbox.review.title": "等你审批",
  "inbox.review.body": "有 {count} 条请求等你审批。",
  "inbox.review.action": "去审批",

  // ================================================================ 审批
  "reviews.title": "审批",
  "reviews.description": "等待审批的新决策、修订和停用请求。",
  "reviews.tab.mine": "待我审批",
  "reviews.tab.submitted": "我提交的",
  "reviews.empty.title": "没有请求",
  "reviews.empty.hint": "提议的新决策、修订和停用请求会在这里等待审批。",
  "reviews.empty.drafts": "去草稿",
  "reviews.empty.submitted": "没有你提交的请求",
  "reviews.kind.decision": "新建",
  "reviews.kind.amendment": "修订",
  "reviews.kind.retirement": "停用",
  "reviews.submitted": "{actor} 提交 · {date}",
  "reviews.approvals": "{have}/{required} 票",
  "reviews.cascade.warning": "将停用 {count} 条约束",
  "reviews.cascade.title": "批准后会发生什么",
  "reviews.cascade.retires": "{id} 将不再生效",
  "reviews.cascade.replacedBy": "由 {id} 接替",
  "reviews.cascade.blocking": "{id} 目前会阻断合并。停用后不再阻断。",
  "reviews.cascade.none": "批准它不会让任何生效中的约束失效。",
  "reviews.approve.decision": "批准",
  "reviews.approve.amendment": "批准修订",
  "reviews.approve.retirement": "批准停用",
  "reviews.reject": "驳回",
  "reviews.declined": "已驳回。",
  "reviews.withdraw": "撤回",
  "reviews.withdrawn": "已撤回。提案会回到草稿。",
  "reviews.voted": "你已批准。",
  "reviews.applied": "已批准并生效。",
  "reviews.pending": "已记录你的批准：{have}/{required}。",
  "reviews.selfApproval": "这是你提交的。审批规则允许你自己批准。",
  "reviews.selfApprovalBlocked": "这是你提交的。审批规则要求由其他人批准。",
  "reviews.amend.title": "改了什么",
  "reviews.amend.added": "新增",
  "reviews.amend.note": "已接受的措辞不会被改写，只能追加。",
  "reviews.create.rationale": "理由",
  "reviews.create.noRationale": "没有理由。起草它的智能体宁可留空也不编造。批准之前把理由补上。",
  "reviews.retirement.reason": "停用原因",

  // ================================================================ 草稿
  "drafts.title": "草稿",
  "drafts.description": "智能体整理好的事实。用你自己的话改写后再提交。没人在等。",
  "drafts.empty.title": "没有草稿",
  "drafts.empty.hint": "从仓库里读出的候选、以及从 Pull Request 起草的决策，都会在这里等你。",
  "drafts.fromRepository": "来自仓库",
  "drafts.fromRepository.hint": "引自你已经写下的文件，等你确认。",
  "drafts.fromCode": "来自代码",
  "drafts.fromCode.hint": "结构有了，理由没有。",
  "drafts.fromPeople": "你自己的草稿",
  "drafts.fromPeople.hint": "手写的，还没提交。",
  "drafts.needs.reason": "缺理由",
  "drafts.needs.confirm": "待确认",
  "drafts.confirm": "确认",
  "drafts.reject": "这不是决策",
  "drafts.edit": "完善",
  "drafts.confidence": "置信度 {percent}%",
  "drafts.quoted": "引自 {source}",
  "drafts.rationaleEmpty": "每一条起草记录的理由都是空的。智能体只陈述它能证明的，理由留给你。",

  // ============================================================ 我的变更
  "myChanges.title": "我的变更",
  "myChanges.description": "你的 Pull Request 有没有踩到已有的决策。",
  "myChanges.empty.title": "没有已检查的变更",
  "myChanges.empty.hint": "开启检查后，每个 Pull Request 都会显示检查结果。这一页展示长期趋势。",
  "myChanges.empty.action": "开启检查",
  "myChanges.badge.clean": "符合 {count} 条决策",
  "myChanges.badge.noFindings": "无冲突",
  "myChanges.badge.conflict": "{count} 处冲突",
  "myChanges.pattern.title": "你触发了同一条约束 {count} 次",
  "myChanges.pattern.body": "{id} — {statement}",
  "myChanges.pattern.question": "是你总忘，还是它的范围太宽？",
  "myChanges.pattern.amend": "提议修订",

  // ================================================================ 健康
  "health.title": "健康",
  "health.description": "记忆中可能已过时的地方。空着说明一切正常。",
  "health.coverage.title": "代码地图覆盖率",
  "health.coverage.claimed": "{percent}% 的变更路径已映射到模块",
  "health.coverage.unclaimed": "{count} 个未映射",
  "health.coverage.ceiling": "这个数字决定了 Pull Request 检查和提问能覆盖多少。未映射的路径无法检查，提问也无从回答。",
  "health.coverage.action": "映射这些路径",
  "health.coverage.none": "没有路径映射到模块。",
  "health.empty.title": "没有过时的内容",
  "health.empty.hint": "陈旧决策、孤立约束、反复豁免的约束，要等记忆运行一段时间才会出现。",
  "health.stale.title": "陈旧",
  "health.orphan.title": "孤立约束",
  "health.waived.title": "反复豁免",
  "health.unenforced.title": "未执行的约束",
  "health.hypothesis": "漂移是待核实的假设，它从不自行改动任何决策。",
  "health.confidence": "置信度 {percent}%",

  // ================================================================ 设置
  "settings.title": "设置",
  "settings.group.personal": "个人",
  "settings.group.workspace": "工作区",
  "settings.preferences.title": "偏好设置",
  "settings.preferences.description": "保存在你的账户里，换一台设备也一样。",
  "settings.preferences.saved": "偏好设置已更新。",
  "settings.preferences.appearanceHint": "跟随系统会随设备的浅色或深色设置切换。",
  "settings.profile.title": "个人资料",
  "settings.profile.description": "其他人在 Diotia 里看到的你。",
  "settings.profile.avatar": "头像",
  "settings.profile.name": "称呼",
  "settings.profile.username": "用户名",
  "settings.profile.usernameTaken": "这个用户名已被使用。",
  "settings.profile.usernameInvalid": "用 3–30 个字母、数字、下划线或点。",
  "settings.profile.saved": "个人资料已更新。",
  "settings.workspace.title": "通用",
  "settings.workspace.description": "名称、URL 标识和头像。",
  "settings.workspace.avatar": "头像",
  "avatar.upload": "上传图片",
  "avatar.remove": "移除",
  "avatar.unreadable": "无法读取这张图片。换一个 PNG、JPEG 或 WebP 文件。",
  "settings.workspace.save": "存储更改",
  "settings.workspace.saved": "工作区已更新。",
  "settings.workspace.readOnly": "只有所有者和管理员可以修改这些设置。",
  "settings.approval.title": "审批规则",
  "settings.approval.description": "每类请求需要几个人批准才生效。",
  "settings.approval.defaults": "当前是默认规则，从未修改过。",
  "settings.approval.selfApprove": "提出人可以批准自己的请求",
  "settings.approval.create": "新建决策",
  "settings.approval.amend": "修订",
  "settings.approval.retire": "停用",
  "settings.approval.people.one": "{count} 人",
  "settings.approval.people.other": "{count} 人",
  "settings.approval.saved": "审批规则已更新。",
  "settings.approval.fixed": "固定规则",
  "settings.approval.agents": "智能体",
  "settings.approval.agentsValue": "可以起草和提议，但它们的批准不计票。",
  "settings.approval.blocking": "阻断合并",
  "settings.approval.blockingValue": "只有人能让约束阻断合并，而且只限可自动检查的约束。",
  "settings.repositories.title": "仓库",
};
